import Dexie, {Table} from 'dexie';
import {SyncStatusItem} from '../interfaces/core/sync-status-item';
import {ServiceFacility} from '../interfaces/core/service-facility';
import {Customer} from '../interfaces/customers/customer';
import {WmWoundQuestion} from '../interfaces/wound-management/wm-wound-question';
import {WmWoundLocation} from '../interfaces/wound-management/wm-wound-location';
import {WmWoundFinishedReason} from '../interfaces/wound-management/wm-wound-finished-reason';
import {CdMdsArea} from '../interfaces/care/cd-rai/cd-mds-area';
import {CdAimEvaluationReason} from '../interfaces/care/cd-care-plan/cd-aim-evaluation-reason';
import {CdAimEvaluationScale} from '../interfaces/care/cd-care-plan/cd-aim-evaluation-scale';
import {CdRai} from '../interfaces/care/cd-rai/cd-rai';
import {CdMdsAnswer} from '../interfaces/care/cd-rai/cd-mds-answer';
import {SyncItem} from '../interfaces/core/sync-item';
import {WmWoundDocumentation} from '../interfaces/wound-management/wm-wound-documentation';
import {WmWoundDocumentationEntry} from '../interfaces/wound-management/wm-wound-documentation-entry';
import {WmWoundAnswer} from '../interfaces/wound-management/wm-wound-answer';
import {WmWoundImage} from '../interfaces/wound-management/wm-wound-image';
import {CdCarePlan} from '../interfaces/care/cd-care-plan/cd-care-plan';
import {CdAimEvaluation} from '../interfaces/care/cd-care-plan/cd-aim-evaluation';
import {CdAddedAim} from '../interfaces/care/cd-care-plan/cd-added-aim';
import {CdExecutionProof} from '../interfaces/care/cd-execution-proof/cd-execution-proof';
import {CdTemporaryMeasure} from '../interfaces/care/cd-execution-proof/cd-temporary-measure';
import {StorageItem} from '../interfaces/core/storage-item';
import {TableName} from '../enums/core/table-name';
import {CdAimEvaluationOrderOption} from '../interfaces/care/cd-care-plan/cd-aim-evaluation-order-option';
import {PrProcessReport} from '../interfaces/process-reports/pr-process-report';
import {ErrorLog} from '../interfaces/core/error-log';
import {EmployeeRoster} from '../interfaces/employee-rosters/employee-roster';
import {OpOperatingPlanItem} from '../interfaces/operating-plan/op-operating-plan-item';
import {SyncTime} from '../interfaces/core/sync-time';
import {Vehicle} from '../interfaces/vehicles/vehicle';
import {TtTravelEntry} from '../interfaces/time-tracking/tt-travel-entry';
import {TtActivityType} from '../interfaces/time-tracking/tt-activity-type';
import {Service} from '../interfaces/core/service';
import {TtActivityEntry} from '../interfaces/time-tracking/tt-activity-entry';
import {Employee} from '../interfaces/core/employee';
import {ReRelationType} from '../interfaces/relatives/re-relation-type';
import {TtWorkingTimeEntry} from '../interfaces/time-tracking/tt-working-time-entry';
import {TtWorkingTimeType} from '../interfaces/time-tracking/tt-working-time-type';
import {Product} from '../interfaces/product-management/product';
import {TtProductEntry} from '../interfaces/time-tracking/tt-product-entry';
import {Instruction} from '../interfaces/instructions/instruction';
import {CustomerDocumentType} from '../interfaces/customer-documents/customer-document-type';
import {CustomerDocument} from '../interfaces/customer-documents/customer-document';
import {ProdRequest} from '../interfaces/product-management/prod-request';
import {VitalSignContext} from '../interfaces/vital-signs/vital-sign-context';
import {VitalSignEntry} from '../interfaces/vital-signs/vital-sign-entry';
import {VitalSignThreshold} from '../interfaces/vital-signs/vital-sign-threshold';
import {VitalSignOption} from '../interfaces/vital-signs/vital-sign-option';
import {VitalSignType} from '../interfaces/vital-signs/vital-sign-type';
import {TtLiveActivityEntry} from '../interfaces/time-tracking/tt-live-activity-entry';
import {PrType} from '../interfaces/process-reports/pr-type';
import {TtDeviationReason} from '../interfaces/time-tracking/tt-deviation-reason';
import {CdTask} from '../interfaces/care/cd-execution-proof/cd-task';
import {CdEpSkipReason} from '../interfaces/care/cd-execution-proof/cd-ep-skip-reason';
import {CdExecutionProofDraft} from '../interfaces/care/cd-execution-proof/cd-execution-proof-draft';
import {OpPeriod} from '../enums/operating-plan/op-period';
import {CdoCustomerRequirement} from '../interfaces/customer-documents/cdo-customer-requirement';
import {CdoDocumentRequest} from '../interfaces/customer-documents/cdo-document-request';
import {CdoRequirementStatus} from '../interfaces/customer-documents/cdo-requirement-status';
import {VoiceRecording} from '../interfaces/core/voice-recording';

interface TableEntry {
  [tableName: string]: string
}

class DBService extends Dexie {

  public storage!: Table<StorageItem, string>;
  public syncStatusItems!: Table<SyncStatusItem, number>;
  public serviceFacilities!: Table<ServiceFacility, number>;
  public customers!: Table<Customer, number>;
  public syncTimes!: Table<SyncTime, number>;
  public services!: Table<Service, number>;
  public employees!: Table<Employee, number>;
  public reRelationTypes!: Table<ReRelationType, number>;
  public products!: Table<Product, number>;
  public opPeriods!: Table<OpPeriod, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Wound documentation
  // -------------------------------------------------------------------------------------------------------------------
  public wmWoundQuestions!: Table<WmWoundQuestion, number>;
  public wmWoundLocations!: Table<WmWoundLocation, number>;
  public wmWoundFinishedReasons!: Table<WmWoundFinishedReason, number>;

  public wmWoundDocumentations!: Table<WmWoundDocumentation, string>;
  public wmWoundDocumentationEntries!: Table<WmWoundDocumentationEntry, string>;
  public wmWoundAnswers!: Table<WmWoundAnswer, string>;
  public wmWoundImages!: Table<WmWoundImage, string>;

  // -------------------------------------------------------------------------------------------------------------------
  // Care
  // -------------------------------------------------------------------------------------------------------------------
  public cdMdsAreas!: Table<CdMdsArea, number>;
  public cdAimEvaluationReasons!: Table<CdAimEvaluationReason, number>;
  public cdAimEvaluationScales!: Table<CdAimEvaluationScale, number>;
  public cdAimEvaluationOrderOptions!: Table<CdAimEvaluationOrderOption, number>;

  public cdRais!: Table<CdRai, string>;
  public cdMdsAnswers!: Table<CdMdsAnswer, string>;
  public cdCarePlans!: Table<CdCarePlan, number>;
  public cdAimEvaluations!: Table<CdAimEvaluation, string>;
  public cdAddedAims!: Table<CdAddedAim, number>;
  public cdExecutionProofDrafts!: Table<CdExecutionProofDraft, number>;
  public cdExecutionProofs!: Table<CdExecutionProof, string>;
  public cdTemporaryMeasures!: Table<CdTemporaryMeasure, string>;
  public cdTasks!: Table<CdTask, number>;
  public cdEpSkipReasons!: Table<CdEpSkipReason, number>;
  // ---------------------------------------------------------------------------------------------------------------
  // Time tracking
  // ---------------------------------------------------------------------------------------------------------------
  public vehicles!: Table<Vehicle, number>;
  public ttTravelEntries!: Table<TtTravelEntry, string>;
  public ttActivityTypes!: Table<TtActivityType, number>;
  public ttActivityEntries!: Table<TtActivityEntry, string>;
  public ttWorkingTimeEntries!: Table<TtWorkingTimeEntry, number>;
  public ttWorkingTimeTypes!: Table<TtWorkingTimeType, number>;
  public ttProductEntries!: Table<TtProductEntry, string>;
  public ttLiveActivityEntries!: Table<TtLiveActivityEntry, string>;
  public ttDeviationReasons!: Table<TtDeviationReason, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Employees roster
  // -------------------------------------------------------------------------------------------------------------------
  public employeeRosters!: Table<EmployeeRoster, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Operating plan
  // -------------------------------------------------------------------------------------------------------------------
  public opOperatingPlanItems!: Table<OpOperatingPlanItem, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Error handling
  // -------------------------------------------------------------------------------------------------------------------
  public errorLogs!: Table<ErrorLog, string>;

  // -------------------------------------------------------------------------------------------------------------------
  // Voice recordings
  // -------------------------------------------------------------------------------------------------------------------
  public voiceRecordings!: Table<VoiceRecording, string>;

  // -------------------------------------------------------------------------------------------------------------------
  // Process reports
  // -------------------------------------------------------------------------------------------------------------------
  public prProcessReports!: Table<PrProcessReport, string>;
  public prTypes!: Table<PrType, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Instructions
  // -------------------------------------------------------------------------------------------------------------------
  public instructions!: Table<Instruction, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // customer documents
  // -------------------------------------------------------------------------------------------------------------------
  public customerDocumentTypes!: Table<CustomerDocumentType, number>;
  public customerDocuments!: Table<CustomerDocument, string>;
  public cdoCustomerRequirements!: Table<CdoCustomerRequirement, string>;
  public cdoDocumentRequests!: Table<CdoDocumentRequest, string>;
  public cdoRequirementStatusItems!: Table<CdoRequirementStatus, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // product management
  // -------------------------------------------------------------------------------------------------------------------
  public prodRequests!: Table<ProdRequest, string>;

  // -------------------------------------------------------------------------------------------------------------------
  // vital sign
  // -------------------------------------------------------------------------------------------------------------------
  public vitalSignContexts!: Table<VitalSignContext, number>;
  public vitalSignTypes!: Table<VitalSignType, number>;
  public vitalSignOptions!: Table<VitalSignOption, number>;
  public vitalSignThresholds!: Table<VitalSignThreshold, number>;
  public vitalSignEntries!: Table<VitalSignEntry, string>;

  /**
   *
   */
  constructor() {
    super('Tables');

    const tables: TableEntry = {
      storage: '&key',
      syncStatusItems: '++id, lastSync, tableName, &primaryKey, isSynced, [tableName+isSynced], deleted, hasConflict',
      serviceFacilities: '&id',
      customers: '&id, serviceFacilityId, fullName',
      audioRecords: '++id',
      syncTimes: '++id, tableName',
      services: '&id',
      employees: '&id, fullName, serviceFacilityId',
      reRelationTypes: '&id',
      products: '&id',
      opPeriods: '&id',
      // ---------------------------------------------------------------------------------------------------------------
      // Wound documentation
      // ---------------------------------------------------------------------------------------------------------------
      wmWoundQuestions: '&id',
      wmWoundLocations: '&id',
      wmWoundFinishedReasons: '&id',
      wmWoundDocumentations: '&uuid, customerId, createdDate',
      wmWoundDocumentationEntries: '&uuid, wmWoundDocumentationUuid, createdDate, created',
      wmWoundAnswers: '&uuid, wmWoundDocumentationEntryUuid, wmWoundQuestionId',
      wmWoundImages: '&uuid, wmWoundDocumentationEntryUuid, created',

      // ---------------------------------------------------------------------------------------------------------------
      // Care
      // ---------------------------------------------------------------------------------------------------------------
      cdMdsAreas: '&id',
      cdAimEvaluationReasons: '&id',
      cdAimEvaluationOrderOptions: '&id',
      cdAimEvaluationScales: '&value',
      cdRais: '&uuid, customerId, date',
      cdMdsAnswers: '&uuid, cdRaiUuid, cdRaiMdsQuestionId',
      cdCarePlans: '&id, customerId, createdDate',
      cdAimEvaluations: '&uuid, cdAddedAimId',
      cdAddedAims: '&id, cdCarePlanId',
      cdExecutionProofDrafts: '++id, customerId, cdCarePlanId, start',
      cdExecutionProofs: '&uuid, customerId, cdCarePlanId, start',
      cdTasks: '&id, customerId',
      cdEpSkipReasons: '&id',
      cdTemporaryMeasures: '&uuid, customerId',
      // ---------------------------------------------------------------------------------------------------------------
      // Time tracking
      // ---------------------------------------------------------------------------------------------------------------
      vehicles: '&id, serviceFacilityId, licensePlate',
      ttTravelEntries: '&uuid, date',
      ttActivityTypes: '&id, sort, name, [sort+name]',
      ttWorkingTimeTypes: '&id',
      ttActivityEntries: '&uuid, timeFrom, timeTo, opOperatingPlanItemId, ttWorkingTimeTypeId',
      ttWorkingTimeEntries: '&id, timeFrom, timeTo',
      ttProductEntries: '&uuid, ttActivityEntryUuid, employeeId',
      ttLiveActivityEntries: '&uuid',
      ttDeviationReasons: '&id',

      // ---------------------------------------------------------------------------------------------------------------
      // Process reports
      // ---------------------------------------------------------------------------------------------------------------
      prProcessReports: '&uuid, id, customerId, created, userId',
      prTypes: '&id, forCustomers, forEmployees, forServiceFacilities, isDefault',

      // ---------------------------------------------------------------------------------------------------------------
      // Employees roster
      // ---------------------------------------------------------------------------------------------------------------
      employeeRosters: '&id, date',

      // ---------------------------------------------------------------------------------------------------------------
      // Operating plan
      // ---------------------------------------------------------------------------------------------------------------
      opOperatingPlanItems: '++id, employeeId, start',

      // ---------------------------------------------------------------------------------------------------------------
      // Instructions
      // ---------------------------------------------------------------------------------------------------------------
      instructions: '&id, created',

      // ---------------------------------------------------------------------------------------------------------------
      // Customer documents
      // ---------------------------------------------------------------------------------------------------------------
      customerDocumentTypes: '&id, name',
      customerDocuments: '&uuid, customerId, created',

      // ---------------------------------------------------------------------------------------------------------------
      // Product management
      // ---------------------------------------------------------------------------------------------------------------
      prodRequests: '&uuid, customerId, created',

      // ---------------------------------------------------------------------------------------------------------------
      // Vital signs
      // ---------------------------------------------------------------------------------------------------------------
      vitalSignContexts: '&id, vitalSignTypeId',
      vitalSignTypes: '&id',
      vitalSignOptions: '&id, vitalSignTypeId',
      vitalSignThresholds: '&id, customerId, vitalSignTypeId, [customerId+vitalSignTypeId]',
      vitalSignEntries: '&uuid, customerId, vitalSignTypeId, [customerId+vitalSignTypeId]',

      // ---------------------------------------------------------------------------------------------------------------
      // Error handling
      // ---------------------------------------------------------------------------------------------------------------
      errorLogs: '&uuid'
    };

    // Outstanding customer documents:
    // the two synchronized records plus the server calculated snapshot
    const cdoRequirementTables: TableEntry = {
      cdoCustomerRequirements: '&uuid, customerId, [customerId+cdoDocumentTypeId]',
      cdoDocumentRequests: '&uuid, customerId, created',
      cdoRequirementStatusItems: '++id, customerId'
    };

    const prProcessReportsWithIdPrimaryKey = '&id, customerId, created, userId';
    const prProcessReportsWithUuidPrimaryKey = tables['prProcessReports'];
    // Historical schema of cdTemporaryMeasures (before the customerId index) — versions 50-53 must
    // keep declaring the old schema, otherwise Dexie computes an empty diff for installations
    // already on version 53 and the customerId index would never be created.
    const cdTemporaryMeasuresWithoutCustomerIdIndex = '&uuid, cdCarePlanId';
    const cdTemporaryMeasuresWithCarePlanIdIndex = '&uuid, cdCarePlanId, customerId';

    this.version(50).stores({
      ...tables,
      prProcessReports: prProcessReportsWithIdPrimaryKey,
      cdTemporaryMeasures: cdTemporaryMeasuresWithoutCustomerIdIndex
    });

    this.version(51).stores({
      ...tables,
      prProcessReports: null,
      cdTemporaryMeasures: cdTemporaryMeasuresWithoutCustomerIdIndex
    });

    this.version(52).stores({
      ...tables,
      prProcessReports: prProcessReportsWithUuidPrimaryKey,
      cdTemporaryMeasures: cdTemporaryMeasuresWithoutCustomerIdIndex
    });

    // Adds the execution-proof schema for installations that already upgraded to version 52 on develop.
    this.version(53).stores({
      ...tables,
      cdTemporaryMeasures: cdTemporaryMeasuresWithoutCustomerIdIndex
    });

    // Adds the customerId index on cdTemporaryMeasures for plan-less (customer-level) temporary measures.
    this.version(54).stores({
      ...tables,
      cdTemporaryMeasures: cdTemporaryMeasuresWithCarePlanIdIndex
    });

    // Temporary measures are customer-owned. The coordinated rollout starts
    // directly with this schema, so no client-side data mapping is required.
    this.version(55).stores({
      ...tables,
      cdHiddenMeasures: null
    });

    // Reconciles the schemas introduced independently on develop and the execution-proof branch.
    // Existing installations can arrive here from develop version 54 or feature version 55.
    this.version(56).stores({
      ...tables,
      prProcessReports: prProcessReportsWithUuidPrimaryKey,
      voiceRecordings: '&uuid, created',
      audioRecords: null,
      ...cdoRequirementTables,
      cdHiddenMeasures: null
    }).upgrade(async (transaction) => {
      // The content of customerDocumentTypes changes from the legacy customer
      // document type ids to the cdo ones. The stale rows would be read with
      // the new cdo tables until the first basic-data sync replaces them, so
      // they are dropped here - the next sync refills the store.
      await transaction.table('customerDocumentTypes').clear();
    });
  }

  /**
   *
   * @param tableName
   */
  public getSyncTableByTableName(tableName: TableName): Table<SyncItem, string> {
    if(this.hasOwnProperty(tableName)) {
      // @ts-ignore
      return this[tableName];
    }
    return null;
  }

}

export const db = new DBService();
